import type { Product } from "@/types";
import {
  primaryImage,
  variantPrice,
  defaultVariant,
} from "@/lib/product-helpers";

/**
 * DRB-67 — ONE product matcher, used by the search page AND the header autocomplete.
 *
 * The search page previously inlined its own rule: join name + descriptions + condition +
 * category into a string and test `.includes(q)` on the whole query. That means "pure cbd" finds
 * nothing while "cbd pure" finds the product, because a single substring test cannot match tokens
 * out of order. Two surfaces implementing search separately would drift the same way the money
 * calculation did, so both now call this.
 *
 * Ranking is deliberate rather than incidental: a term in the NAME beats the same term buried in
 * a long description, or the dropdown surfaces the wrong product first for a term that appears in
 * both.
 */
export interface SearchHit {
  product: Product;
  score: number;
  thumbnail: string;
  priceCents: number;
}

const norm = (s: unknown) =>
  String(s ?? "")
    .toLowerCase()
    .trim();

/** Every token must match somewhere (AND), so extra words narrow rather than widen. */
export function searchProducts(
  products: readonly Product[],
  rawQuery: string,
  max?: number,
): SearchHit[] {
  const tokens = norm(rawQuery).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const hits: SearchHit[] = [];
  for (const p of products) {
    const name = norm(p.name);
    const meta = [norm(p.condition), norm(p.category)].join(" ");
    const body = [norm(p.shortDescription), norm(p.longDescription)].join(" ");
    const haystack = `${name} ${meta} ${body}`;

    if (!tokens.every((t) => haystack.includes(t))) continue;

    let score = 0;
    for (const t of tokens) {
      if (name.startsWith(t))
        score += 100; // strongest: the name begins with it
      else if (name.includes(t)) score += 60;
      if (meta.includes(t)) score += 20;
      if (body.includes(t)) score += 5;
    }
    const v = defaultVariant(p);
    hits.push({
      product: p,
      score,
      thumbnail: primaryImage(p),
      priceCents: v ? variantPrice(p, v) : (p.salePrice ?? p.basePrice),
    });
  }

  hits.sort(
    (a, b) => b.score - a.score || a.product.name.localeCompare(b.product.name),
  );
  return typeof max === "number" ? hits.slice(0, max) : hits;
}
